/**
 * React NodeView for `statusLozenge` — Phase 4b inline atomic chip.
 *
 * Visual: a small pill rendered inline with surrounding prose, with a
 * `bg-{color}-100 text-{color}-700` fill (dark themes: `bg-{color}-900/40
 * text-{color}-200`). Click opens `<StatusEditPopover>` for in-place
 * editing.
 *
 * Layout:
 *
 *   ┌─ NodeViewWrapper as="span" ─────────────────────────┐
 *   │  ┌─ button ──────────────────────┐                    │
 *   │  │  pill body — "In Progress"    │                    │
 *   │  └───────────────────────────────┘                    │
 *   │  (when popover open)                                   │
 *   │  ┌─ StatusEditPopover ──────────────────────────────┐ │
 *   │  │  Color swatches + text input                     │ │
 *   │  └──────────────────────────────────────────────────┘ │
 *   └────────────────────────────────────────────────────────┘
 *
 * Empty-text fallback: when `node.attrs.text === ''`, render the
 * localized color name (`wiki.status.colors.<color>`) so the chip always
 * has visible text. Editing the text to a blank string after the user
 * has committed an explicit label persists the empty string AND the
 * fallback render kicks in — same UX as a fresh insert.
 */

import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StatusEditPopover } from './StatusEditPopover'
import {
  DEFAULT_STATUS_COLOR,
  type StatusColor,
  isStatusColor,
  resolveStatusColorClasses,
} from './status-kinds'

export function StatusLozengeView({ node, updateAttributes }: ReactNodeViewProps<HTMLElement>) {
  const { t } = useTranslation('wiki')
  const rawColor = (node.attrs.color as string) ?? DEFAULT_STATUS_COLOR
  const color: StatusColor = isStatusColor(rawColor) ? rawColor : DEFAULT_STATUS_COLOR
  const text = (node.attrs.text as string) ?? ''

  const [popoverOpen, setPopoverOpen] = useState(false)

  const colorClasses = resolveStatusColorClasses(color)
  const ariaLabel = t('statusLozenge.ariaLabel', { defaultValue: 'Status lozenge' })
  const editAriaLabel = t('statusLozenge.editAriaLabel', { defaultValue: 'Edit status' })

  // Empty-text fallback: use the localized color name so the chip is
  // never visually blank. The fallback is rendered text, not a
  // placeholder pseudo-element, so screen readers also pick it up.
  const displayText =
    text.length > 0 ? text : t(`statusLozenge.colors.${color}`, { defaultValue: color })

  const onPillMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    // mousedown + preventDefault so the surrounding contenteditable
    // doesn't fight us for the selection. The popover gets the focus
    // after mount.
    e.preventDefault()
    e.stopPropagation()
    setPopoverOpen((o) => !o)
  }, [])

  const onChange = useCallback(
    (next: { color?: StatusColor; text?: string }) => {
      updateAttributes(next)
    },
    [updateAttributes],
  )

  return (
    <NodeViewWrapper
      as="span"
      data-block-type="status-lozenge"
      data-color={color}
      // `relative` so the popover positions against this wrapper.
      // `inline-block` keeps the chip on the inline baseline.
      className="relative inline-block align-baseline"
    >
      <button
        type="button"
        onMouseDown={onPillMouseDown}
        aria-label={`${ariaLabel}: ${displayText}`}
        title={editAriaLabel}
        className={`wiki-inline-chip ${colorClasses}`}
      >
        {displayText}
      </button>
      {popoverOpen ? (
        <StatusEditPopover
          color={color}
          text={text}
          onChange={onChange}
          onClose={() => setPopoverOpen(false)}
        />
      ) : null}
    </NodeViewWrapper>
  )
}

StatusLozengeView.displayName = 'StatusLozengeView'
